import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { epApi, showError } from '../api';
import { addDays, md, mdy, RECOMMENDER_STATUS, todayIso } from '../format';
import { useLoad, useLocalState } from '../hooks';
import { TaskView, TrackedProgram } from '../types';
import { DeadlineField } from './DeadlineOverride';
import './alltasks.css';

/**
 * All tasks: every task for every program (and her own tasks) in one
 * spreadsheet-style list. Group by date, program, type or status; reorder,
 * resize, show or hide columns; wrap text. The view is remembered on this
 * device. Click a task to open it in the side panel.
 *
 * Who can do what (the server checks the same rules):
 * - student: everything on her list;
 * - counselor: reads everything, adds tasks for her, changes only tasks they added;
 * - parent: reads everything (no percentages) and can send a reminder.
 */
export type Viewer = 'student' | 'counselor' | 'parent';
type Col = 'prog' | 'kind' | 'src' | 'due' | 'dl' | 'status';
type Group = 'due' | 'prog' | 'kind' | 'status';

const COLS: Record<Col, string> = { prog: 'Program', kind: 'Type', src: 'From', due: 'My target', dl: 'Deadline', status: 'Status' };
const WIDTH: Record<Col | 'task', number> = { task: 280, prog: 190, kind: 130, src: 110, due: 150, dl: 140, status: 140 };
const STATUS: [string, string][] = [['not_started', 'To do'], ['in_progress', 'In progress'], ['done', 'Done']];
const STATUS_LABEL: Record<string, string> = Object.fromEntries(STATUS);
const OPEN = ['not_started', 'in_progress', 'snooze'];

interface ViewPrefs { group: Group; order: Col[]; hidden: Col[]; width: Partial<Record<Col | 'task', number>>; wrap: boolean; showDone: boolean }
const DEFAULT_PREFS: ViewPrefs = { group: 'due', order: ['prog', 'kind', 'src', 'due', 'dl', 'status'], hidden: [], width: {}, wrap: false, showDone: false };

/** Short type label from the checklist key. Her own tasks have no key. */
export function kindOf(t: TaskView): string {
  const k = (t.requirement_key ?? '').replace(/^legacy:/, '');
  if (!k) return t.essay_prompt ? 'Essay' : t.recommender_name ? 'Recommendation' : 'My task';
  if (k.startsWith('essay')) return 'Essay';
  if (k.startsWith('rec')) return 'Recommendation';
  if (k.startsWith('transcript')) return 'Transcript';
  if (k.startsWith('test')) return 'Test scores';
  if (k.startsWith('resume')) return 'Resume';
  if (k.startsWith('interview')) return 'Interview';
  if (k.startsWith('fee')) return 'Fee';
  if (k.startsWith('submit')) return 'Submit';
  if (k.startsWith('decide') || k.startsWith('decision')) return 'Decision';
  if (k.startsWith('attend')) return 'Attending';
  return 'Application';
}
const fromOf = (t: TaskView): [string, string] =>
  t.origin === 'program_requirement' ? ['Program', 'a'] : t.created_by_role === 'counselor' ? ['Counselor', 'w'] : ['You', ''];
const effective = (t: TaskView) => t.student_target_date || t.official_deadline || null;
const isMine = (t: TaskView) => t.origin !== 'program_requirement';
/** The deadline a program has posted, read from its steps (earliest one still carrying it). */
const postedFor = (all: TaskView[], pid: number) =>
  all.filter((t) => t.program_id === pid && t.official_deadline && t.deadline_source === 'gradmap').map((t) => t.official_deadline as string).sort()[0] ?? null;
const daysBetween = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 864e5);

export function bucket(t: TaskView, today: string): [number, string] {
  const d = effective(t);
  if (t.status === 'done') return [6, 'Done'];
  if (!d) return [5, 'No date yet'];
  if (d < today) return [0, 'Overdue'];
  if (d <= addDays(today, 6)) return [1, 'This week'];
  if (d <= addDays(today, 13)) return [2, 'Next week'];
  if (d <= addDays(today, 45)) return [3, 'Later this season'];
  return [4, 'Further out'];
}

interface Props {
  studentId: number;
  viewer: Viewer;
  studentName?: string;
  tracked: TrackedProgram[];
  /** After a change, so the page can refresh counts and the calendar. */
  onChanged?: () => void;
}

export default function AllTasks({ studentId, viewer, studentName, tracked, onChanged }: Props) {
  const who = studentName || 'the student';
  const today = todayIso();
  const tasks = useLoad<TaskView[]>(() => epApi.tasks(studentId), [studentId]);
  const [prefs, setPrefs] = useLocalState<ViewPrefs>(`ep.alltasks.${viewer}`, DEFAULT_PREFS);
  const [q, setQ] = useState('');
  const [pf, setPf] = useState<number | ''>('');
  const [onlyIds, setOnlyIds] = useState<Set<number> | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [colsOpen, setColsOpen] = useState(false);
  const [panel, setPanel] = useState<{ task: TaskView | null; programId: number | null } | null>(null);
  const [dragCol, setDragCol] = useState<Col | null>(null);

  const names = useMemo(() => new Map(tracked.map((p) => [p.program_id, p.name])), [tracked]);
  const progName = (id: number | null) => (id ? names.get(id) ?? 'A program' : 'Not tied to a program');
  const order = (prefs.order && prefs.order.length === 6 ? prefs.order : DEFAULT_PREFS.order) as Col[];
  const hidden = new Set(prefs.hidden ?? []);
  const w = (c: Col | 'task') => (prefs.width && prefs.width[c]) || WIDTH[c];
  const set = (patch: Partial<ViewPrefs>) => setPrefs({ ...prefs, ...patch });

  // Program tasks and tasks people added here. Not the dashboard's suggestions.
  const all = useMemo(
    () => (tasks.data ?? []).filter((t) => t.status !== 'not_applicable' && (t.program_id || t.origin === 'student_added' || t.origin === 'counselor_added')),
    [tasks.data],
  );
  const notices = tracked.filter((p) => p.update_notice);

  const rows = all.filter(
    (t) =>
      (prefs.showDone || t.status !== 'done') &&
      (pf === '' || t.program_id === pf) &&
      (!onlyIds || onlyIds.has(t.id)) &&
      (!q || `${t.title} ${progName(t.program_id)}`.toLowerCase().includes(q.toLowerCase())),
  );
  const groupKey = (t: TaskView): [number | string, string] => {
    if (prefs.group === 'prog') return [t.program_id ? progName(t.program_id) : '~', progName(t.program_id)];
    if (prefs.group === 'kind') return [kindOf(t), kindOf(t)];
    if (prefs.group === 'status') {
      const i = ['in_progress', 'not_started', 'snooze', 'done'].indexOf(t.status);
      return [i, STATUS_LABEL[t.status] ?? 'Later'];
    }
    return bucket(t, today);
  };
  const groups: { sort: number | string; label: string; items: TaskView[]; pid: number | null }[] = [];
  {
    const m = new Map<string, { sort: number | string; label: string; items: TaskView[]; pid: number | null }>();
    for (const t of rows) {
      const [s, l] = groupKey(t);
      if (!m.has(l)) m.set(l, { sort: s, label: l, items: [], pid: prefs.group === 'prog' ? t.program_id : null });
      m.get(l)!.items.push(t);
    }
    groups.push(...Array.from(m.values()).sort((a, b) => (typeof a.sort === 'number' && typeof b.sort === 'number' ? a.sort - b.sort : String(a.sort).localeCompare(String(b.sort)))));
    groups.forEach((g) => g.items.sort((a, b) => (effective(a) ?? '9999').localeCompare(effective(b) ?? '9999')));
  }

  const cols = order.filter((c) => !hidden.has(c) && !(prefs.group === 'prog' && c === 'prog'));

  // Analytics at the top. Parents see counts, never percentages.
  const done = all.filter((t) => t.status === 'done').length;
  const open = all.filter((t) => OPEN.includes(t.status));
  const week = open
    .filter((t) => { const d = effective(t); return !!d && d >= today && d <= addDays(today, 6); })
    .sort((a, b) => (effective(a) ?? '').localeCompare(effective(b) ?? ''));
  const overdue = open.filter((t) => { const d = effective(t); return !!d && d < today; });
  const nextDl = tracked
    .filter((p) => p.next_deadline && p.next_deadline >= today)
    .sort((a, b) => (a.next_deadline ?? '').localeCompare(b.next_deadline ?? ''))[0];
  const daysTo = nextDl && nextDl.next_deadline ? daysBetween(today, nextDl.next_deadline) : 0;

  const patch = async (t: TaskView, data: Partial<TaskView>) => {
    try {
      const saved = await epApi.updateTask(studentId, t.id, data);
      tasks.setData((tasks.data ?? []).map((x) => (x.id === saved.id ? saved : x)));
      onChanged?.();
    } catch (e) {
      showError(e);
    }
  };
  const tick = (t: TaskView) => patch(t, { status: t.status === 'done' ? 'not_started' : 'done' });

  // Resize a column by dragging its edge (mouse, pen or finger). Double-click resets.
  const startResize = (c: Col | 'task', e: React.PointerEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const start = e.clientX;
    const base = w(c);
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    let last = base;
    const move = (ev: PointerEvent) => {
      last = Math.max(90, Math.min(600, base + ev.clientX - start));
      const col = document.querySelector(`.at-tbl col[data-c="${c}"]`) as HTMLElement | null;
      if (col) col.style.width = `${last}px`;
    };
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      set({ width: { ...prefs.width, [c]: last } });
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
  };
  const moveCol = (c: Col, dir: number) => {
    const i = order.indexOf(c);
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    set({ order: next });
  };
  const dropOn = (target: Col) => {
    if (!dragCol || dragCol === target) return;
    const next = order.filter((c) => c !== dragCol);
    next.splice(next.indexOf(target), 0, dragCol);
    set({ order: next });
    setDragCol(null);
  };
  const toggleGroup = (label: string) => {
    const n = new Set(collapsed);
    if (n.has(label)) n.delete(label);
    else n.add(label);
    setCollapsed(n);
  };

  const cell = (c: Col, t: TaskView) => {
    if (c === 'prog') return <td key={c}><span className="at-prog">{progName(t.program_id)}</span></td>;
    if (c === 'kind') return <td key={c}><span className="chip">{kindOf(t)}</span></td>;
    if (c === 'src') {
      const [l, tone] = fromOf(t);
      return <td key={c}><span className={`chip ${tone}`}>{l}</span></td>;
    }
    if (c === 'due') {
      const late = !!(t.student_target_date && t.official_deadline && t.student_target_date > t.official_deadline);
      return (
        <td key={c} className="at-due" onClick={(e) => e.stopPropagation()}>
          {t.status === 'done' ? (
            <span className="muted small">Done</span>
          ) : t.can_edit ? (
            <input type="date" className="at-date" aria-label={`Target date for ${t.title}`} value={t.student_target_date ?? ''} onChange={(e) => patch(t, { student_target_date: e.target.value || null })} />
          ) : (
            <span className="at-mine">{t.student_target_date ? md(t.student_target_date) : '-'}</span>
          )}
          {late && t.status !== 'done' && <span className="at-sub red">After the deadline</span>}
        </td>
      );
    }
    if (c === 'dl') {
      if (!t.official_deadline) return <td key={c}><span className="muted small">{t.origin === 'program_requirement' ? 'Not posted yet' : 'None'}</span></td>;
      const days = daysBetween(today, t.official_deadline);
      const sub = t.status === 'done' ? '' : days < 0 ? 'Passed' : days <= 30 ? `In ${days} ${days === 1 ? 'day' : 'days'}` : '';
      return (
        <td key={c} className="at-dlc">
          <span className="at-dl">{md(t.official_deadline)}</span>
          {t.deadline_source === 'override' && <span className="at-sub" title="Set while the program has not posted its deadline">Not official</span>}
          {sub && <span className="at-sub">{sub}</span>}
        </td>
      );
    }
    return (
      <td key={c} className="at-st" onClick={(e) => e.stopPropagation()}>
        {t.can_edit ? (
          <select className={`at-status s-${t.status}`} aria-label={`Status of ${t.title}`} value={STATUS_LABEL[t.status] ? t.status : 'not_started'} onChange={(e) => patch(t, { status: e.target.value })}>
            {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        ) : (
          <span className={`chip ${t.status === 'done' ? 'ok' : t.status === 'in_progress' ? 'a' : ''}`}>{STATUS_LABEL[t.status] ?? 'To do'}</span>
        )}
      </td>
    );
  };

  const span = 2 + cols.length;
  const canAdd = viewer !== 'parent';
  const addLabel = viewer === 'counselor' ? `+ Add a task for ${who}` : '+ Add a task';
  const noticeText = (p: TrackedProgram) => {
    const n = p.update_notice!;
    const parts = [n.added ? `${n.added} new ${n.added === 1 ? 'task' : 'tasks'}` : '', n.dates ? `${n.dates} ${n.dates === 1 ? 'date' : 'dates'} updated` : ''].filter(Boolean).join(', ');
    return ` changed your list: ${parts}.${n.deadline ? ` Deadline: ${mdy(n.deadline)}.` : ''}`;
  };

  return (
    <div className="at">
      <div className="at-stats">
        <div className="at-stat">
          <span className="k">{viewer === 'student' ? 'Your progress' : `${who}'s progress`}</span>
          <span className="v">{done} <small>{`of ${all.length} tasks done`}</small></span>
          {viewer !== 'parent' && <div className="at-meter" aria-hidden="true"><i style={{ width: `${all.length ? (done / all.length) * 100 : 0}%` }} /></div>}
          <span className="s">{viewer === 'student' ? 'Tick a task to see it move' : `Updates as ${who} ticks tasks off`}</span>
        </div>
        <div className="at-stat">
          <span className="k">Due this week</span>
          <span className="v">{week.length} <small>{week.length === 1 ? 'task' : 'tasks'}</small></span>
          {overdue.length > 0 && <span className="s red">{`${overdue.length} overdue`}</span>}
          {week[0] && <span className="s">Next: <b>{week[0].title}</b></span>}
        </div>
        <div className="at-stat">
          <span className="k">Next program deadline</span>
          <span className="v">{nextDl ? md(nextDl.next_deadline) : 'None yet'}{nextDl && <small>{` in ${daysTo} ${daysTo === 1 ? 'day' : 'days'}`}</small>}</span>
          <span className="s">{nextDl ? nextDl.name : 'Dates appear when programs post them'}</span>
        </div>
        <div className="at-stat">
          <span className="k">Programs</span>
          <span className="v">{tracked.length} <small>tracked</small></span>
          <span className="s">{`${open.length} open ${open.length === 1 ? 'task' : 'tasks'}`}</span>
        </div>
      </div>

      {viewer === 'student' &&
        notices.map((p) => (
          <div key={p.program_id} className="at-notice">
            <span className="chip a">Program update</span>
            <div className="at-notice-t"><b>{p.name}</b>{noticeText(p)}</div>
            <div className="at-notice-a">
              <button type="button" className="btn sm" onClick={() => setOnlyIds(new Set([...p.update_notice!.new_task_ids, ...p.update_notice!.changed_task_ids]))}>Show what changed</button>
              <button
                type="button"
                className="btn sm"
                onClick={async () => {
                  try {
                    await epApi.noticeSeen(studentId, p.program_id);
                    onChanged?.();
                  } catch (e) {
                    showError(e);
                  }
                }}
              >
                Got it
              </button>
            </div>
          </div>
        ))}

      <div className="at-bar">
        <span className="lbl" style={{ margin: 0 }}>Group by</span>
        <div className="at-seg" role="group" aria-label="Group by">
          {([['due', 'Date'], ['prog', 'Program'], ['kind', 'Type'], ['status', 'Status']] as [Group, string][]).map(([k, l]) => (
            <button key={k} type="button" className={prefs.group === k ? 'on' : ''} aria-pressed={prefs.group === k} onClick={() => { set({ group: k }); setCollapsed(new Set()); }}>
              {l}
            </button>
          ))}
        </div>
        <input className="in at-search" placeholder="Search tasks" aria-label="Search tasks" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="in at-pf" aria-label="Program" value={pf} onChange={(e) => setPf(e.target.value ? Number(e.target.value) : '')}>
          <option value="">All programs</option>
          {tracked.map((p) => <option key={p.program_id} value={p.program_id}>{p.name}</option>)}
        </select>
      </div>
      <div className="at-bar">
        {canAdd && <button type="button" className="btn primary" onClick={() => setPanel({ task: null, programId: pf === '' ? null : pf })}>{addLabel}</button>}
        <button type="button" className="btn" onClick={() => set({ showDone: !prefs.showDone })}>{prefs.showDone ? 'Hide done' : 'Show done'}</button>
        <button type="button" className="btn" onClick={() => set({ wrap: !prefs.wrap })}>{prefs.wrap ? 'Single line' : 'Wrap text'}</button>
        <div className="at-pop">
          <button type="button" className="btn" aria-expanded={colsOpen} onClick={() => setColsOpen(!colsOpen)}>Columns</button>
          {colsOpen && (
            <div className="at-menu" role="dialog" aria-label="Show and order columns">
              <span className="lbl">Show and order columns</span>
              {order.map((c, i) => (
                <div key={c} className="at-mrow">
                  <label>
                    <input type="checkbox" checked={!hidden.has(c)} onChange={(e) => set({ hidden: e.target.checked ? (prefs.hidden ?? []).filter((x) => x !== c) : [...(prefs.hidden ?? []), c] })} /> {COLS[c]}
                  </label>
                  <button type="button" className="at-mv" aria-label={`Move ${COLS[c]} left`} disabled={i === 0} onClick={() => moveCol(c, -1)}>↑</button>
                  <button type="button" className="at-mv" aria-label={`Move ${COLS[c]} right`} disabled={i === order.length - 1} onClick={() => moveCol(c, 1)}>↓</button>
                </div>
              ))}
              <div className="at-mfoot">
                <button type="button" className="btn sm" onClick={() => { setPrefs(DEFAULT_PREFS); setColsOpen(false); }}>Reset the view</button>
                <button type="button" className="btn sm" onClick={() => setColsOpen(false)}>Done</button>
              </div>
            </div>
          )}
        </div>
        <span className="muted small at-hint">Drag a column header to move it, or drag its edge to resize.</span>
      </div>

      {onlyIds && (
        <div className="at-only">
          Showing what changed.
          <button type="button" className="btn sm" onClick={() => setOnlyIds(null)}>Show all tasks</button>
        </div>
      )}

      <div className="card at-card">
        <div className="at-scroll">
          <table className={`at-tbl ${prefs.wrap ? 'wrap' : ''}`}>
            <colgroup>
              <col style={{ width: 46 }} />
              <col data-c="task" style={{ width: w('task') }} />
              {cols.map((c) => <col key={c} data-c={c} style={{ width: w(c) }} />)}
            </colgroup>
            <thead>
              <tr>
                <th aria-label="Done" />
                <th>
                  Task
                  <span className="at-grip" onPointerDown={(e) => startResize('task', e)} onDoubleClick={() => set({ width: { ...prefs.width, task: WIDTH.task } })} />
                </th>
                {cols.map((c) => (
                  <th
                    key={c}
                    draggable
                    onDragStart={() => setDragCol(c)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => dropOn(c)}
                    onDragEnd={() => setDragCol(null)}
                    className={dragCol === c ? 'dragging' : ''}
                  >
                    {COLS[c]}
                    <span className="at-grip" draggable={false} onPointerDown={(e) => startResize(c, e)} onDoubleClick={() => set({ width: { ...prefs.width, [c]: WIDTH[c] } })} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tasks.loading && !tasks.data && (
                <tr><td colSpan={span} className="muted at-empty">Loading</td></tr>
              )}
              {tasks.data && groups.length === 0 && (
                <tr><td colSpan={span} className="body at-empty">{all.length ? 'No tasks match. Try fewer filters.' : 'No tasks yet. Add a program to Tracking to get its checklist, or add your own task.'}</td></tr>
              )}
              {groups.map((g) => {
                const shut = collapsed.has(g.label);
                const pAll = g.pid ? all.filter((t) => t.program_id === g.pid) : [];
                const pDone = pAll.filter((t) => t.status === 'done').length;
                return (
                  <React.Fragment key={g.label}>
                    <tr className={`at-g ${g.label === 'Overdue' ? 'overdue' : ''}`} onClick={() => toggleGroup(g.label)}>
                      <td colSpan={span}>
                        {shut ? '▸' : '▾'} {g.label}
                        {prefs.group === 'prog' && g.pid ? <span className="n">{` · ${pDone} of ${pAll.length} done`}</span> : <span className="n">{g.items.length}</span>}
                      </td>
                    </tr>
                    {!shut &&
                      g.items.map((t) => {
                        const meta = [
                          t.links && t.links.length ? `${t.links.length} ${t.links.length === 1 ? 'link' : 'links'}` : '',
                          t.notes ? 'Notes' : '',
                          t.recommender_name ? t.recommender_name : '',
                          t.word_limit ? `${t.word_limit} words` : '',
                        ].filter(Boolean).join(' · ');
                        return (
                          <tr key={t.id} className={`at-task ${t.status === 'done' ? 'done' : ''}`} onClick={() => setPanel({ task: t, programId: t.program_id })}>
                            <td onClick={(e) => e.stopPropagation()}>
                              {t.can_edit ? (
                                <button type="button" className={`tick ${t.status === 'done' ? 'on' : ''}`} aria-label={t.status === 'done' ? `Mark ${t.title} not done` : `Mark ${t.title} done`} onClick={() => tick(t)}>
                                  {t.status === 'done' ? '✓' : ''}
                                </button>
                              ) : (
                                <span className={`tick ${t.status === 'done' ? 'on' : ''}`} aria-hidden="true">{t.status === 'done' ? '✓' : ''}</span>
                              )}
                            </td>
                            <td>
                              <span className="at-name">{t.title}</span>
                              {meta && <span className="at-meta">{meta}</span>}
                              <span className="at-mprog">{t.program_id ? progName(t.program_id) : ''}</span>
                            </td>
                            {cols.map((col) => cell(col, t))}
                          </tr>
                        );
                      })}
                    {!shut && prefs.group === 'prog' && canAdd && (
                      <tr className="at-addrow">
                        <td />
                        <td colSpan={span - 1}>
                          <button type="button" className="btn sm ghost" onClick={() => setPanel({ task: null, programId: g.pid })}>
                            {viewer === 'counselor' ? `+ Add a task for ${who}` : '+ Add a task to this program'}
                          </button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {prefs.group !== 'prog' && canAdd && groups.length > 0 && (
                <tr className="at-addrow">
                  <td />
                  <td colSpan={span - 1}>
                    <button type="button" className="btn sm ghost" onClick={() => setPanel({ task: null, programId: pf === '' ? null : pf })}>{addLabel}</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {panel && (
        <TaskPanel
          studentId={studentId}
          viewer={viewer}
          who={who}
          task={panel.task}
          programId={panel.programId}
          tracked={tracked}
          onClose={() => setPanel(null)}
          onRefresh={() => { tasks.reload(); onChanged?.(); }}
          postedDeadline={panel.task?.program_id ? postedFor(tasks.data ?? [], panel.task.program_id) : null}
          onSaved={(saved, removed) => {
            const list = tasks.data ?? [];
            tasks.setData(removed ? list.filter((x) => x.id !== saved.id) : list.some((x) => x.id === saved.id) ? list.map((x) => (x.id === saved.id ? saved : x)) : [...list, saved]);
            setPanel(null);
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}

interface PanelProps {
  studentId: number;
  viewer: Viewer;
  who: string;
  task: TaskView | null;
  programId: number | null;
  tracked: TrackedProgram[];
  onClose: () => void;
  onSaved: (t: TaskView, removed?: boolean) => void;
  /** A change that touched other tasks too (a deadline set for the whole program). */
  onRefresh?: () => void;
  /** The program's posted deadline (from its other steps), if any. */
  postedDeadline?: string | null;
}

const withHttp = (u: string) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

/** The side panel: task, program, status, target date, deadline, essay, recommender, links, notes. */
function TaskPanel({ studentId, viewer, who, task, programId, tracked, onClose, onSaved, onRefresh, postedDeadline = null }: PanelProps) {
  const [shown, setShown] = useState<TaskView | null>(null);
  const isNew = !task;
  const own = isNew || (task ? isMine(task) : false);
  const canEdit = isNew ? viewer !== 'parent' : !!task && task.can_edit;
  const [f, setF] = useState({
    title: task ? task.title : '',
    program_id: (task ? task.program_id ?? '' : programId ?? '') as number | '',
    status: task && STATUS_LABEL[task.status] ? task.status : 'not_started',
    student_target_date: (task && task.student_target_date) || '',
    official_deadline: (task && task.official_deadline) || '',
    draft_link: (task && task.draft_link) || '',
    recommender_name: (task && task.recommender_name) || '',
    recommender_email: (task && task.recommender_email) || '',
    recommender_status: (task && task.recommender_status) || 'not_asked',
    notes: (task && task.notes) || '',
    links: task ? task.links ?? (task.link ? [task.link] : []) : ([] as string[]),
  });
  const [newLink, setNewLink] = useState('');
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement | null>(null);
  const essay = task ? kindOf(task) === 'Essay' : false;
  const letter = task ? kindOf(task) === 'Recommendation' : false;
  const progName = (id: number | '' | null) => (id ? tracked.find((p) => p.program_id === id)?.name ?? 'A program' : 'Not tied to a program');

  useEffect(() => {
    first.current?.focus();
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const addLink = () => {
    const u = newLink.trim();
    if (!u) return;
    setF({ ...f, links: [...f.links, withHttp(u)] });
    setNewLink('');
  };

  const save = async () => {
    if (isNew && !f.title.trim()) {
      toast.error('Give the task a name.');
      return;
    }
    setBusy(true);
    try {
      const links = newLink.trim() ? [...f.links, withHttp(newLink.trim())] : f.links;
      if (isNew) {
        const created = await epApi.createTask(studentId, {
          title: f.title.trim(),
          program_id: f.program_id === '' ? undefined : f.program_id,
          student_target_date: f.student_target_date || undefined,
          official_deadline: f.official_deadline || undefined,
          notes: f.notes.trim() || undefined,
          links: links.length ? links : undefined,
        });
        toast.success(viewer === 'counselor' ? `Added to ${who}'s tasks` : 'Task added');
        onSaved(created);
        return;
      }
      const data: any = { status: f.status, student_target_date: f.student_target_date || null, notes: f.notes, links };
      if (own) {
        data.title = f.title.trim() || task!.title;
        data.official_deadline = f.official_deadline || null;
      }
      if (essay) data.draft_link = f.draft_link.trim() || null;
      if (letter) Object.assign(data, { recommender_name: f.recommender_name, recommender_email: f.recommender_email, recommender_status: f.recommender_status });
      const saved = await epApi.updateTask(studentId, task!.id, data);
      toast.success('Saved');
      onSaved(saved);
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!task) return;
    try {
      const r = await epApi.removeTask(studentId, task.id);
      toast.success('Task removed');
      onSaved(r, true);
    } catch (e) {
      showError(e);
    }
  };
  const nudge = async () => {
    if (!task) return;
    try {
      await epApi.nudgeTask(studentId, task.id);
      toast.success(`Reminder sent to ${who}`);
      onClose();
    } catch (e) {
      showError(e);
    }
  };

  return (
    <>
      <div className="at-scrim" onClick={onClose} />
      <aside className="at-panel" role="dialog" aria-label={task ? task.title : 'Add a task'}>
        <div className="at-ph">
          <div style={{ minWidth: 0 }}>
            <span className="lbl" style={{ margin: 0 }}>{task ? progName(task.program_id) : 'New task'}</span>
            <h2 style={{ marginTop: 2 }}>{task ? task.title : viewer === 'counselor' ? `Add a task for ${who}` : 'Add a task'}</h2>
          </div>
          <button type="button" className="at-x" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="at-pb">
          {!canEdit && (
            <div className="panel" style={{ marginBottom: 14 }}>
              {viewer === 'parent' ? `You can see ${who}'s tasks. Only ${who} can change them.` : `${who} owns this task. You can see it but not change it.`}
            </div>
          )}
          <div className="at-f">
            <label className="lbl" htmlFor="at-title">Task</label>
            {own && canEdit ? (
              <input id="at-title" ref={first} className="in" value={f.title} placeholder="What needs to get done?" onChange={(e) => setF({ ...f, title: e.target.value })} />
            ) : (
              <div className="at-ro">
                {task && task.title}
                {task && task.origin === 'program_requirement' && <small>{`From ${progName(task.program_id)}'s requirements. You can add your own notes, links and date.`}</small>}
                {task && task.created_by_role === 'counselor' && <small>Added by the counselor</small>}
              </div>
            )}
          </div>
          <div className="at-two">
            <div className="at-f">
              <label className="lbl" htmlFor="at-prog">Program</label>
              {isNew ? (
                <select id="at-prog" className="in" value={f.program_id} onChange={(e) => setF({ ...f, program_id: e.target.value ? Number(e.target.value) : '' })}>
                  <option value="">Not tied to a program</option>
                  {tracked.map((p) => <option key={p.program_id} value={p.program_id}>{p.name}</option>)}
                </select>
              ) : (
                <div className="at-ro">{progName(task!.program_id)}</div>
              )}
            </div>
            {!isNew && (
              <div className="at-f">
                <label className="lbl" htmlFor="at-status">Status</label>
                <select id="at-status" className="in" disabled={!canEdit} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="at-two">
            <div className="at-f">
              <label className="lbl" htmlFor="at-target">{viewer === 'student' ? 'Your target date' : 'Target date'}</label>
              <input id="at-target" type="date" className="in" disabled={!canEdit} value={f.student_target_date} onChange={(e) => setF({ ...f, student_target_date: e.target.value })} />
            </div>
            <div className="at-f">
              <label className="lbl" htmlFor="at-dl">Deadline</label>
              {own && canEdit ? (
                <input id="at-dl" type="date" className="in" value={f.official_deadline} onChange={(e) => setF({ ...f, official_deadline: e.target.value })} />
              ) : task ? (
                <DeadlineField
                  task={shown ?? task}
                  programDeadline={postedDeadline}
                  studentId={studentId}
                  canSet={viewer !== 'parent'}
                  onChanged={(pv) => { const t = pv.checklist.find((x) => x.id === task.id); if (t) setShown(t); onRefresh?.(); }}
                />
              ) : null}
            </div>
          </div>
          {essay && task && (
            <div className="at-box">
              <span className="lbl">Essay prompt</span>
              <div className="at-q">{task.essay_prompt || 'Not posted yet.'}</div>
              {task.word_limit ? <div className="muted small" style={{ marginTop: 6 }}>{`${task.word_limit} words`}</div> : null}
              <label className="lbl" htmlFor="at-draft" style={{ marginTop: 10 }}>Link to the draft</label>
              <input id="at-draft" className="in" disabled={!canEdit} value={f.draft_link} placeholder="Paste a Google Doc or Word link" onChange={(e) => setF({ ...f, draft_link: e.target.value })} />
            </div>
          )}
          {letter && task && (
            <div className="at-box">
              <span className="lbl">Recommender</span>
              <div className="at-two">
                <input className="in" aria-label="Recommender name" disabled={!canEdit} placeholder="Name" value={f.recommender_name} onChange={(e) => setF({ ...f, recommender_name: e.target.value })} />
                <input className="in" aria-label="Recommender email" disabled={!canEdit} placeholder="Email" value={f.recommender_email} onChange={(e) => setF({ ...f, recommender_email: e.target.value })} />
              </div>
              <label className="lbl" htmlFor="at-rs" style={{ marginTop: 10 }}>Where it stands</label>
              <select id="at-rs" className="in" disabled={!canEdit} value={f.recommender_status} onChange={(e) => setF({ ...f, recommender_status: e.target.value })}>
                {RECOMMENDER_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          )}
          <div className="at-f">
            <span className="lbl">Links</span>
            {f.links.map((u, i) => (
              <div key={`${u}-${i}`} className="at-link">
                <a href={u} target="_blank" rel="noopener noreferrer">{u.replace(/^https?:\/\//, '')}</a>
                {canEdit && <button type="button" className="at-x" aria-label="Remove link" onClick={() => setF({ ...f, links: f.links.filter((_, j) => j !== i) })}>✕</button>}
              </div>
            ))}
            {canEdit && (
              <div className="at-addlink">
                <input className="in" aria-label="Add a link" placeholder="Paste a link: portal, doc, website" value={newLink} onChange={(e) => setNewLink(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }} />
                <button type="button" className="btn sm" onClick={addLink}>Add</button>
              </div>
            )}
            {!canEdit && f.links.length === 0 && <span className="muted small">No links yet</span>}
          </div>
          <div className="at-f">
            <label className="lbl" htmlFor="at-notes">Notes or description</label>
            <textarea id="at-notes" className="in at-ta" rows={4} disabled={!canEdit} placeholder="Usernames (never passwords), what to bring, who to ask" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
        </div>
        <div className="at-pfoot">
          <div>{!isNew && own && canEdit && <button type="button" className="btn" onClick={remove}>Remove task</button>}</div>
          <div className="at-pfa">
            <button type="button" className="btn" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</button>
            {canEdit && <button type="button" className="btn primary" disabled={busy} onClick={save}>{isNew ? 'Add task' : 'Save'}</button>}
            {!canEdit && viewer === 'parent' && task && task.status !== 'done' && <button type="button" className="btn primary" onClick={nudge}>{`Send ${who} a reminder`}</button>}
          </div>
        </div>
      </aside>
    </>
  );
}
